import React, {useEffect, useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  Body,
  Button,
  SwitchButton,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import SortField, {
  DefaultSortField,
} from '@/types/models/creator-crm/sortField';
import {SortDirection, SortingState} from '@/types/models/creator-crm/sorting';
import type {Segment} from '@/types/models/segments/segment';
import type {SortDir} from '@/types/sorting';
import {VIEW_TYPES, ViewType} from '@/types/viewType';
import ActionDrawerButtons from '@/views/creators/creator-crm/components/action-drawer-buttons';
import getAnalyticsSearchScreenName from '@/views/creators/creator-crm/utils/getAnalyticsSearchScreenName';
import Divider from '@/components/divider';
import {SortByDropdown} from '@/components/dropdown';
import SegmentsButton from '@/components/segments-button';
import TotalResults from '@/components/total-results';
import TypeSelector from '@/components/type-selector';
import useTrackSearchButtonEvent from '@/hooks/analytics/use-track-search-button-event';
import useCreatorCrmStore, {
  creatorCrmActions,
  VIEW_TYPE_GRID,
  VIEW_TYPE_TABLE,
} from '@/stores/creator-crm/creatorCrmStore';
import {shouldShowSortField} from '@/stores/creator-crm/utils/filterVisibilityUtils';
import UnifiedSearchInput from '../unified-search-input';
import styles from './SearchControls.module.scss';

type SearchControlsProps = {
  totalResults: number;
  viewType: ViewType;
  onActionDrawerButtonClick?: (label: string) => void;
  onSegmentDeleted?: (segment: Segment) => void;
  onSelectSegment: (segment: Segment) => void;
  onSegmentsButtonOpen?: () => void;
  onSortByOpen?: () => void;
  sorting?: SortingState;
  selectedPlatform: string | null;
  columnSelector?: React.ReactNode;
  segments: Segment[];
  activeSegment: Segment | null;
  /** When provided (e.g. Creator CRM), the popper marks this campaign segment as active. */
  activeCampaignSegment?: Segment | null;
  isLoadingSegments: boolean;
  organizationName: string;
  isSelectMode?: boolean;
  onToggleSelectMode?: () => void;
  isCrmMode?: boolean;
  onToggleCrmMode?: () => void;
  isSearchMode?: boolean;
};

const analyticsButtonName: Record<string, string> = {
  [VIEW_TYPE_TABLE]: 'cards-view',
  [VIEW_TYPE_GRID]: 'list-view',
};

function getFollowersLocaleLabelKey(platform: string | null) {
  if (platform === null) {
    return 'Followers / Subscribers';
  }
  if (platform === 'youtube') {
    return 'Subscribers';
  }

  return 'Followers';
}

const SORT_TYPES = (platform: null | string) =>
  [
    {
      id: SortField.rating,
      labelLocaleKey: 'Rating',
    },
    {
      id: SortField.followers,
      labelLocaleKey: getFollowersLocaleLabelKey(platform),
    },
    platform === 'youtube'
      ? null
      : {
          id: SortField.engagementRate,
          labelLocaleKey: 'Engagement Rate',
        },
    {
      id: SortField.relevancy,
      labelLocaleKey: 'Relevancy',
    },
    {
      id: SortField.vettingDecision,
      labelLocaleKey: 'Safe Collab Status',
    },
  ].filter(Boolean) as Array<{id: string; labelLocaleKey: string}>;

function SearchControls({
  totalResults,
  viewType,
  onActionDrawerButtonClick,
  onSegmentDeleted,
  onSelectSegment,
  onSegmentsButtonOpen,
  onSortByOpen,
  sorting,
  selectedPlatform,
  columnSelector,
  segments,
  activeSegment,
  activeCampaignSegment,
  isLoadingSegments,
  organizationName,
  isSelectMode,
  onToggleSelectMode,
  isCrmMode,
  onToggleCrmMode,
  isSearchMode,
}: SearchControlsProps) {
  const trackSearchButtonEvent = useTrackSearchButtonEvent();
  const searchState = useCreatorCrmStore(
    useShallow((state) => state.content.filtersState.searchState)
  ) || {type: 'all', chips: []};
  const sortTypes = useMemo(
    () => SORT_TYPES(selectedPlatform),
    [selectedPlatform]
  );

  const options = useMemo(() => {
    const hasSearch = searchState.chips.length > 0;
    return sortTypes
      .filter((sortType) => (hasSearch ? true : sortType.id !== 'relevancy'))
      .filter((sortType) => shouldShowSortField(sortType.id, isSearchMode))
      .map((sortType) => ({
        id: sortType.id,
        label: translate(sortType.labelLocaleKey),
      }));
  }, [searchState.chips.length, sortTypes, isSearchMode]);

  useEffect(() => {
    // edge case handling => if sorting field is not in the options, reset to default
    if (sorting && !options.find((option) => option.id === sorting?.field)) {
      creatorCrmActions.changeSorting(DefaultSortField);
    }
  }, [sorting, options]);

  return (
    <div className={styles.searchControls}>
      <div className={styles.left}>
        {isCrmMode !== undefined && onToggleCrmMode ? (
          <div className={styles.crmToggle}>
            <Body
              size="sm"
              color={designSystemToken(
                isCrmMode ? 'semantic.fg.secondary' : 'semantic.fg.primary'
              )}
            >
              {translate('Search')}
            </Body>
            <SwitchButton
              checked={isCrmMode}
              onChange={onToggleCrmMode}
              checkedTrackColor={designSystemToken('semantic.fg.primary')}
              uncheckedTrackColor={designSystemToken('semantic.fg.primary')}
            />
            <Body
              size="sm"
              color={designSystemToken(
                isCrmMode ? 'semantic.fg.primary' : 'semantic.fg.secondary'
              )}
            >
              {translate('CRM')}
            </Body>
          </div>
        ) : null}
        <UnifiedSearchInput
          className={styles.unifiedSearchInput}
          searchType={searchState.type}
          onSearchTypeChange={(type) =>
            creatorCrmActions.updateSearchType(type)
          }
          chips={searchState.chips}
          onChange={(searchType, chips) => {
            if (searchState.type !== searchType) {
              creatorCrmActions.updateSearchType(searchType);
            }
            creatorCrmActions.updateSearchChips(chips);
          }}
          placeholder={translate('Search Organization CRM', {
            name: organizationName,
          })}
        />
        <SegmentsButton
          segments={segments}
          activeSegment={activeSegment}
          activeCampaignSegment={activeCampaignSegment}
          isLoadingSegments={isLoadingSegments}
          onSegmentDeleted={onSegmentDeleted}
          onSelectSegment={onSelectSegment}
          onOpen={onSegmentsButtonOpen}
        />
        <ActionDrawerButtons
          onActionDrawerButtonClick={onActionDrawerButtonClick}
        />
      </div>
      <div className={styles.right}>
        <TotalResults
          amount={totalResults}
          searchTerm={
            searchState.chips.length > 0
              ? searchState.chips.join(', ')
              : undefined
          }
        />
        <Divider vertical height={20} margin={8} />
        {viewType === VIEW_TYPE_GRID && (
          <SortByDropdown
            className={styles.sortByDropdownTrigger}
            options={options}
            sortBy={sorting?.field}
            sortDir={sorting?.direction?.toLowerCase() as SortDir | undefined}
            onChange={(id, dir) => {
              creatorCrmActions.changeSorting(
                id,
                dir.toUpperCase() as SortDirection
              );
            }}
            onOpen={onSortByOpen}
          />
        )}
        {columnSelector != null && (
          <div className={styles.columnSelectorSlot}>{columnSelector}</div>
        )}
        {viewType === VIEW_TYPE_GRID && onToggleSelectMode ? (
          <Button
            size="small"
            variant="text"
            onClick={onToggleSelectMode}
            className={styles.selectButton}
            startIcon={
              <Icon
                appearance="neutral"
                name={isSelectMode ? 'Features-Check-Filled' : 'Features-Check'}
                color={designSystemToken('semantic.fg.inverse-secondary')}
              />
            }
          >
            <Body
              size="sm"
              className={styles.selectButtonText}
              color={designSystemToken('semantic.fg.primary')}
            >
              {translate(isSelectMode ? 'Selecting' : 'Select')}
            </Body>
          </Button>
        ) : null}
        <TypeSelector
          types={VIEW_TYPES}
          selectedTypeId={viewType}
          backgroundColor={designSystemToken(
            'semantic.bg.neutral-inverse-secondary'
          )}
          onChange={(id) => {
            if (id === viewType) {
              return;
            }
            trackSearchButtonEvent.pressed({
              button_name: analyticsButtonName[viewType],
              screen_name: getAnalyticsSearchScreenName(viewType),
            });
            creatorCrmActions.changeViewType(id as ViewType);
          }}
        />
      </div>
    </div>
  );
}

export default SearchControls;
